import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScheduleForm } from '@/components/manufacturing/ScheduleForm';
import { toast } from '@/hooks/use-toast';

// Mock schedule data
const mockSchedule = {
  id: '1',
  title: 'Widget Assembly Schedule',
  description: 'Production schedule for widget assembly line',
  startDate: '2024-01-15T08:00',
  endDate: '2024-01-15T16:00',
  priority: 'high',
  status: 'scheduled',
  productionJobId: '1',
  workOrderIds: ['1', '2'],
  resources: [
    {
      resourceId: '1',
      resourceType: 'machine',
      quantity: 1,
      allocatedHours: 8,
      startTime: '08:00',
      endTime: '16:00',
    },
    {
      resourceId: '3',
      resourceType: 'operator',
      quantity: 2,
      allocatedHours: 8,
      startTime: '08:00',
      endTime: '16:00',
    },
  ],
  shifts: [
    {
      shiftId: '1',
      date: '2024-01-15',
      startTime: '08:00',
      endTime: '16:00',
      operatorIds: ['1', '2'],
    },
  ],
  constraints: {
    maxConcurrentJobs: 3,
    bufferTime: 30,
    allowOvertime: false,
    requireQualityCheck: true,
  },
  notes: 'High priority production run for Q1 targets',
};

export default function EditSchedule() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [schedule, setSchedule] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (id === '1') {
          setSchedule(mockSchedule);
        } else {
          throw new Error('Schedule not found');
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load schedule. Please try again.',
          variant: 'destructive',
        });
        navigate('/manufacturing/schedule');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSchedule();
    }
  }, [id, navigate]);

  const handleSubmit = async (data: any) => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Updating schedule:', { id, ...data });
      
      toast({
        title: 'Schedule Updated',
        description: `Schedule "${data.title}" has been updated successfully.`,
      });
      
      // Navigate back to schedule detail page
      navigate(`/manufacturing/schedule/${id}`);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update schedule. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              <span>Loading schedule...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-gray-500">Schedule not found</p>
              <Button
                onClick={() => navigate('/manufacturing/schedule')}
                className="mt-4"
              >
                Back to Schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/manufacturing/schedule/${id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Schedule
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Production Schedule</h1>
            <p className="text-gray-600">
              Modify schedule details and resource allocation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-500" />
          <span className="text-sm text-gray-500">
            Last modified: {new Date().toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Schedule Info */}
      <Card>
        <CardHeader>
          <CardTitle>Schedule Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Schedule ID:</span>
              <p className="text-gray-600">SCH-{schedule.id.padStart(3, '0')}</p>
            </div>
            <div>
              <span className="font-medium">Current Status:</span>
              <p className="text-gray-600 capitalize">{schedule.status}</p>
            </div>
            <div>
              <span className="font-medium">Priority:</span>
              <p className="text-gray-600 capitalize">{schedule.priority}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Schedule Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScheduleForm 
            initialData={schedule}
            onSubmit={handleSubmit}
            isLoading={saving}
          />
        </CardContent>
      </Card>
    </div>
  );
} 